import React, { useCallback, useMemo, useState } from "react";
import {
  View,
  FlatList,
  Pressable,
  ActivityIndicator,
  StyleSheet,
} from "react-native";
import { useFocusEffect } from "@react-navigation/native";
import DsHeader from "@components/ds/DsHeader";
import SingleDatePicker from "@components/SingleDatePicker";
import { showApiError } from "@components/ds/showApiError";
import { C, RText, Tag } from "@components/ds/theme";
import { t } from "@constants/localization";
import { getOpenRecons } from "@services/api/reconList";
import CollectCashSheet from "./CollectCashSheet";
import {
  applyVerified,
  collectedCashErrorKey,
  formatMoney,
  splitDbText,
  toYmd,
} from "./helpers";

const PATH = "ReconList";

// Registered as `ReconList`; opened from Delivery Management's "Recon List"
// row (dark store CMO CPs). The partner-web recon list's Open
// Recon tab on the phone: one row per delivery boy with an open recon on the
// picked OFD date, and a Collect Cash button that records the cash the DB
// hands over before recon closes (amount → OTP read off the DB app → verify).
const ReconList = ({ navigation }) => {
  const [date, setDate] = useState(() => new Date());
  const ymd = useMemo(() => toYmd(date), [date]);
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [sheetRow, setSheetRow] = useState(null);

  const reload = useCallback(
    async ({ refreshing = false } = {}) => {
      try {
        if (refreshing) setIsRefreshing(true);
        else setIsLoading(true);
        const response = await getOpenRecons({ date: ymd });
        setRows(response?.data?.data?.reconsList || []);
      } catch (error) {
        showApiError(error, collectedCashErrorKey);
      } finally {
        setIsLoading(false);
        setIsRefreshing(false);
      }
    },
    [ymd],
  );

  useFocusEffect(
    useCallback(() => {
      reload();
    }, [reload]),
  );

  const onDateChanged = (next) => {
    if (!next) return;
    setRows([]);
    setDate(next);
  };

  // A verified handover updates its card at once; the reload on close
  // catches the rest (a request left pending shows its dot).
  const onCollected = useCallback(
    (result) => {
      if (!sheetRow) return;
      setRows((prev) => applyVerified(prev, sheetRow.db_id, result));
    },
    [sheetRow],
  );

  const closeSheet = useCallback(() => {
    setSheetRow(null);
    reload({ refreshing: true });
  }, [reload]);

  const renderItem = ({ item }) => {
    const { name, phone } = splitDbText(item.db_text);
    const collected = Number(item.cash_collected_early || 0);
    return (
      <View testID={`recon-list-db-${item.db_id}`} style={styles.card}>
        <View style={styles.info}>
          <RText size={16} weight={700} numberOfLines={1}>
            {name || String(item.db_id)}
          </RText>
          {phone ? (
            <RText size={12} weight={500} color={C.muted}>
              {phone}
            </RText>
          ) : null}
          <RText size={12.5} weight={600} color={C.muted}>
            {t(`${PATH}/expected`, {
              amount: formatMoney(item.cash_expected),
            })}
          </RText>
          <RText
            size={12.5}
            weight={600}
            color={collected > 0 ? C.green : C.muted}
          >
            {t(`${PATH}/collectedEarly`, { amount: formatMoney(collected) })}
          </RText>
          {item.pending_cash_request ? (
            <Tag
              label={t(`${PATH}/otpPending`)}
              bg={C.blueBg}
              color={C.blue}
              size={10}
              style={styles.pending}
            />
          ) : null}
        </View>
        <Pressable
          testID={`recon-list-collect-${item.db_id}`}
          onPress={() => setSheetRow(item)}
          style={styles.collectBtn}
        >
          <RText size={13} weight={600} color={C.red}>
            {t(`${PATH}/collect`)}
          </RText>
        </Pressable>
      </View>
    );
  };

  return (
    <View style={styles.screen}>
      <DsHeader
        title={t(`${PATH}/title`)}
        navigation={navigation}
        testID="recon-list-back"
      />
      <View style={styles.dateRow}>
        <RText size={13} weight={600} color={C.muted}>
          {t(`${PATH}/dateLabel`)}
        </RText>
        <SingleDatePicker
          date={date}
          handleDateChanged={onDateChanged}
          maximumDate={new Date()}
          width={150}
        />
      </View>
      {isLoading && rows.length === 0 ? (
        <ActivityIndicator size="large" color={C.red} style={styles.loader} />
      ) : (
        <FlatList
          testID="recon-list"
          showsVerticalScrollIndicator={false}
          data={rows}
          keyExtractor={(item) => String(item.db_id)}
          renderItem={renderItem}
          refreshing={isRefreshing}
          onRefresh={() => reload({ refreshing: true })}
          contentContainerStyle={styles.listBody}
          ListHeaderComponent={
            <RText size={12} weight={500} color={C.muted} style={styles.helper}>
              {t(`${PATH}/helper`)}
            </RText>
          }
          ListEmptyComponent={
            isLoading ? null : (
              <RText
                size={14}
                weight={500}
                color={C.muted}
                style={styles.empty}
              >
                {t(`${PATH}/empty`)}
              </RText>
            )
          }
        />
      )}
      <CollectCashSheet
        visible={!!sheetRow}
        row={sheetRow}
        onClose={closeSheet}
        onCollected={onCollected}
      />
    </View>
  );
};

export default ReconList;

const styles = StyleSheet.create({
  screen: { flex: 1, backgroundColor: C.page },
  loader: { flex: 1 },
  dateRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingHorizontal: 16,
    paddingVertical: 10,
    backgroundColor: C.surface,
    borderBottomWidth: 1,
    borderBottomColor: C.border,
  },
  listBody: { paddingTop: 12, paddingBottom: 24 },
  helper: { paddingHorizontal: 16, paddingBottom: 12, lineHeight: 18 },
  card: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
    backgroundColor: C.surface,
    borderWidth: 1,
    borderColor: C.border,
    borderRadius: 12,
    padding: 14,
    marginHorizontal: 16,
    marginBottom: 12,
  },
  info: { flex: 1, gap: 4 },
  pending: { marginTop: 3, paddingVertical: 5, paddingHorizontal: 11 },
  collectBtn: {
    borderWidth: 1.5,
    borderColor: C.red,
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 8,
    flexShrink: 0,
  },
  empty: { textAlign: "center", marginTop: 60 },
});
